import React, { useState } from 'react'
import type { ClientDatabase } from '@hpanel/web-common/api/types'

/**
 * Connection details modal — Plan 18 §C.
 *
 * Surfaces DB_HOST / DB_PORT / DB_NAME / DB_USER with copy buttons so a
 * tenant who just created a DB knows immediately where to point their
 * app. Password is NOT shown: it's not in any list/detail response (the
 * server only returns it once at create time). If the user forgot it,
 * the row's "Rotate Password" action is the recovery path.
 *
 * The first entry of `connection_info.hosts` is rendered as the
 * recommended Unix-socket host; remaining entries are listed as TCP
 * fallbacks. Both flow from the central API client / type definition;
 * we don't hardcode them here.
 */

export interface ConnectionDetailsModalProps {
  db: ClientDatabase
  onClose: () => void
}

const copyToClipboard = async (text: string): Promise<boolean> => {
  // navigator.clipboard requires a secure context. The panel runs on
  // HTTPS in prod, but the dev/test surface is plain HTTP — fall back to
  // a textarea + execCommand so copy-buttons work in both environments.
  try {
    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function'
    ) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[ConnectionDetailsModal] navigator.clipboard failed', e)
  }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[ConnectionDetailsModal] execCommand copy failed', e)
    return false
  }
}

interface FieldRowProps {
  label: string
  value: string
  /** Optional secondary line of context shown below the value. */
  helper?: string
  testId: string
}

const FieldRow: React.FC<FieldRowProps> = ({ label, value, helper, testId }) => {
  const [copied, setCopied] = useState(false)
  const onCopy = async () => {
    const ok = await copyToClipboard(value)
    if (!ok) return
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }
  return (
    <div className="grid grid-cols-[8rem_1fr_auto] items-center gap-3">
      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </div>
      <div className="min-w-0">
        <div
          data-testid={`${testId}-value`}
          className="font-mono text-sm text-gray-900 dark:text-gray-100 truncate"
        >
          {value}
        </div>
        {helper && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {helper}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onCopy}
        data-testid={`${testId}-copy`}
        className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

export const ConnectionDetailsModal: React.FC<ConnectionDetailsModalProps> = ({
  db,
  onClose,
}) => {
  const hosts = db.connection_info.hosts
  const primaryHost = hosts[0] ?? 'localhost'
  const fallbackHosts = hosts.slice(1)
  return (
    <div
      data-testid="connection-details-modal"
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
          Connection details
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Use these values from inside your domain's container (for
          example in <span className="font-mono">wp-config.php</span> or
          your <span className="font-mono">.env</span> file).
        </p>

        <div className="space-y-3">
          <FieldRow
            testId="cd-host"
            label="DB_HOST"
            value={primaryHost}
            helper={
              fallbackHosts.length > 0
                ? `Unix-socket fast path. TCP fallback: ${fallbackHosts.join(
                    ', ',
                  )}`
                : 'Unix-socket fast path.'
            }
          />
          {fallbackHosts.map((h) => (
            <FieldRow
              key={h}
              testId={`cd-host-${h}`}
              label="DB_HOST (TCP)"
              value={h}
            />
          ))}
          <FieldRow
            testId="cd-port"
            label="DB_PORT"
            value={String(db.connection_info.port)}
          />
          <FieldRow testId="cd-name" label="DB_NAME" value={db.name} />
          <FieldRow
            testId="cd-user"
            label="DB_USER"
            value={db.username ?? '(see Manage Users)'}
            helper={
              db.username
                ? 'Primary user. Additional users in Manage Users.'
                : undefined
            }
          />
        </div>

        <div
          data-testid="cd-password-note"
          className="mt-4 text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded p-3"
        >
          The password is the one you set when creating the database (or
          this user). It isn't stored in this view for safety. If you
          forgot it, use <span className="font-medium">Rotate Password</span>{' '}
          on the database row to set a new one.
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            data-testid="cd-close"
            className="btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
